import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  IVA,
  fmtMXN,
  fmtPct,
  limitesMonto,
  r2,
  siguienteHabil,
  type ParamsCredito,
  type Producto,
} from "@/lib/operativo";
import { ParametrosCredito } from "./ParametrosCredito";
import { useOperativo } from "@/lib/operativo-store";
import { useState } from "react";

const NUM: { key: keyof Producto; label: string; step?: number }[] = [
  { key: "montoMin", label: "Monto mín." },
  { key: "montoMax", label: "Monto máx." },
  { key: "plazoMin", label: "Pagos mín." },
  { key: "plazoMax", label: "Pagos máx." },
  { key: "tasaAnual", label: "Tasa anual (%)", step: 0.1 },
  { key: "factorMora", label: "Factor mora", step: 0.1 },
  { key: "comisionAperturaPct", label: "Com. apertura (%)", step: 0.1 },
  { key: "diasGracia", label: "Días de gracia (mora)" },
  { key: "aforoMin", label: "Aforo mín.", step: 0.1 },
  { key: "antiguedadMaxAnios", label: "Antigüedad máx. (años)" },
  { key: "penaConvencional", label: "Pena convencional ($)" },
];

export function CatalogoProductos() {
  const {
    productos,
    setProductos,
    festivos,
    setFestivos,
    valorUdi,
    setValorUdi,
    restaurarFabrica,
  } = useOperativo();
  const [nuevoFestivo, setNuevoFestivo] = useState("");
  const [editando, setEditando] = useState<string | null>(null);

  const set = (id: string, patch: Partial<Producto>) =>
    setProductos(productos.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const paramsDe = (p: Producto): ParamsCredito => ({
    ...p.params,
    tasaAnual: p.tasaAnual,
    tasaMoratoria: r2(p.tasaAnual * p.factorMora),
  });

  const aplicarParams = (p: Producto, patch: Partial<ParamsCredito>) => {
    const { tasaAnual, tasaMoratoria, ...resto } = patch;
    const nuevaTasa = tasaAnual ?? p.tasaAnual;
    const patchProd: Partial<Producto> = { params: { ...p.params, ...resto } };
    if (tasaAnual !== undefined) patchProd.tasaAnual = tasaAnual;
    if (tasaMoratoria !== undefined && nuevaTasa > 0)
      patchProd.factorMora = r2(tasaMoratoria / nuevaTasa);
    set(p.id, patchProd);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
            Catálogo de productos
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Parámetros comerciales y de riesgo de cada producto. Todos los campos son editables y
            alimentan el motor de cálculo en vivo. Parámetro global de IVA:{" "}
            <strong className="text-navy">{fmtPct(IVA * 100)}</strong>.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="valor-udi" className="text-xs">
              Valor UDI (MXN)
            </Label>
            <Input
              id="valor-udi"
              type="number"
              inputMode="decimal"
              step={0.00001}
              className="num h-9 w-36"
              value={Number.isNaN(valorUdi) ? "" : valorUdi}
              onChange={(e) => setValorUdi(e.target.valueAsNumber)}
            />
          </div>
          <Button size="sm" variant="outline" onClick={restaurarFabrica}>
            <RotateCcw className="mr-1.5 h-4 w-4" /> Restaurar valores de fábrica
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {productos.map((p) => {
          const lim = limitesMonto(p, valorUdi);
          const abierto = editando === p.id;
          return (
            <Card key={p.id} className="print-block border-navy/15 p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
                  {p.nombre}
                </h3>
                <div className="flex items-center gap-2">
                  {p.esRevolvente && (
                    <Badge className="bg-emerald text-primary-foreground">Línea revolvente</Badge>
                  )}
                  <Badge variant="secondary" className="num">
                    {fmtPct(p.tasaAnual, 0)} anual
                  </Badge>
                  <Button
                    size="sm"
                    variant={abierto ? "secondary" : "outline"}
                    onClick={() => setEditando(abierto ? null : p.id)}
                  >
                    {abierto ? (
                      <ChevronUp className="mr-1.5 h-4 w-4" />
                    ) : (
                      <Pencil className="mr-1.5 h-4 w-4" />
                    )}
                    {abierto ? "Cerrar" : "Editar"}
                  </Button>
                </div>
              </div>

              <dl className="mb-1 grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
                {[
                  {
                    l: "Monto",
                    v: `${fmtMXN(lim.min)} – ${fmtMXN(lim.max)}`,
                    n:
                      lim.maxUdis || lim.minUdis
                        ? `${(lim.maxUdis ?? lim.minUdis)!.toLocaleString("es-MX")} UDIS ≈ ${fmtMXN(
                            lim.maxUdis ? lim.max : lim.min,
                          )} al valor UDI actual`
                        : null,
                  },
                  { l: "Plazo", v: `${p.plazoMin} a ${p.plazoMax} pagos` },
                  { l: "Tasa ordinaria anual", v: fmtPct(p.tasaAnual, 0) },
                  {
                    l: "Tasa moratoria",
                    v: `${fmtPct(p.tasaAnual * p.factorMora, 0)} (${p.factorMora}× ordinaria)`,
                  },
                  { l: "Comisión por apertura", v: `${fmtPct(p.comisionAperturaPct)} + IVA` },
                  {
                    l: "Garantía",
                    v: p.requiereGarantia
                      ? `${p.tipoGarantia} · aforo ${p.aforoMin}:1`
                      : "No aplica",
                    n:
                      p.requiereGarantia && p.antiguedadMaxAnios < 50
                        ? `Antigüedad máx. ${p.antiguedadMaxAnios} años`
                        : null,
                  },
                  {
                    l: "Pago anticipado",
                    v: p.params.permitePagoAnticipado
                      ? p.params.penalidadPrepago
                        ? `Permitido con penalización ${fmtPct(p.params.penalidadPrepagoPct)}`
                        : "Permitido, sin penalización"
                      : "No permitido",
                  },
                  {
                    l: "Pena convencional",
                    v: `${fmtMXN(p.penaConvencional)} + IVA por evento de mora`,
                  },
                  { l: "Días de gracia", v: `${p.diasGracia} días` },
                ].map((r) => (
                  <div
                    key={r.l}
                    className="flex flex-col border-b border-dashed border-navy/10 pb-1.5"
                  >
                    <dt className="text-muted-foreground">{r.l}</dt>
                    <dd className="num text-navy">{r.v}</dd>
                    {r.n && <dd className="num text-[11px] text-muted-foreground">{r.n}</dd>}
                  </div>
                ))}
              </dl>

              {abierto && (
                <>
                  <div className="mt-5 grid gap-3 border-t pt-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor={`${p.id}-minUdis`} className="text-xs">
                        Monto mín. en UDIS (vacío = pesos)
                      </Label>
                      <Input
                        id={`${p.id}-minUdis`}
                        type="number"
                        inputMode="decimal"
                        className="num h-9"
                        value={p.montoMinUdis ?? ""}
                        onChange={(e) =>
                          set(p.id, {
                            montoMinUdis: e.target.value === "" ? null : e.target.valueAsNumber,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${p.id}-maxUdis`} className="text-xs">
                        Monto máx. en UDIS (vacío = pesos)
                      </Label>
                      <Input
                        id={`${p.id}-maxUdis`}
                        type="number"
                        inputMode="decimal"
                        className="num h-9"
                        value={p.montoMaxUdis ?? ""}
                        onChange={(e) =>
                          set(p.id, {
                            montoMaxUdis: e.target.value === "" ? null : e.target.valueAsNumber,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {NUM.map((f) => (
                      <div key={String(f.key)} className="space-y-1">
                        <Label htmlFor={`${p.id}-${String(f.key)}`} className="text-xs">
                          {f.label}
                        </Label>
                        <Input
                          id={`${p.id}-${String(f.key)}`}
                          type="number"
                          inputMode="decimal"
                          step={f.step ?? 1}
                          className="num h-9"
                          value={Number.isNaN(p[f.key] as number) ? "" : (p[f.key] as number)}
                          onChange={(e) =>
                            set(p.id, { [f.key]: e.target.valueAsNumber } as Partial<Producto>)
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center justify-between rounded-md bg-surface px-3 py-2">
                    <Label htmlFor={`${p.id}-gar`} className="text-xs">
                      ¿Requiere garantía?
                    </Label>
                    <Switch
                      id={`${p.id}-gar`}
                      checked={p.requiereGarantia}
                      onCheckedChange={(v) => set(p.id, { requiereGarantia: v })}
                    />
                  </div>
                  {p.requiereGarantia && (
                    <div className="mt-3 space-y-1">
                      <Label htmlFor={`${p.id}-tipogar`} className="text-xs">
                        Tipo de garantía
                      </Label>
                      <Input
                        id={`${p.id}-tipogar`}
                        className="h-9"
                        value={p.tipoGarantia}
                        onChange={(e) => set(p.id, { tipoGarantia: e.target.value.slice(0, 60) })}
                      />
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <p className="text-xs text-muted-foreground">
                      Tasa mensual:{" "}
                      <span className="num text-navy">{fmtPct(p.tasaAnual / 12)}</span> · Tasa
                      moratoria anual:{" "}
                      <span className="num text-destructive">
                        {fmtPct(p.tasaAnual * p.factorMora, 0)}
                      </span>
                    </p>
                    {r2(p.factorMora) !== 2 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => set(p.id, { factorMora: 2 })}
                      >
                        Recalcular moratoria a 2× ({fmtPct(p.tasaAnual * 2, 0)})
                      </Button>
                    )}
                  </div>
                  <div className="mt-5 border-t pt-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-navy">
                      Parámetros y opciones por default
                    </p>
                    <ParametrosCredito
                      idPrefijo={`cat-${p.id}`}
                      params={paramsDe(p)}
                      onChange={(patch) => aplicarParams(p, patch)}
                    />
                  </div>
                </>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-navy" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Días inhábiles (festivos México)
          </h3>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Las fechas de pago que caigan en fin de semana o en un día de esta lista se recorren al
          siguiente día hábil.
        </p>
        <div className="mb-4 flex flex-wrap gap-2">
          {festivos.map((f) => (
            <span
              key={f}
              className="flex items-center gap-1.5 rounded-full bg-navy-soft px-3 py-1 text-xs num text-navy"
            >
              {f}
              <button
                type="button"
                aria-label={`Eliminar ${f}`}
                onClick={() => setFestivos(festivos.filter((x) => x !== f))}
                className="text-navy/60 hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="nuevo-festivo" className="text-xs">
              Agregar día inhábil
            </Label>
            <Input
              id="nuevo-festivo"
              type="date"
              className="h-9 w-48"
              value={nuevoFestivo}
              onChange={(e) => setNuevoFestivo(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (nuevoFestivo && !festivos.includes(nuevoFestivo))
                setFestivos([...festivos, nuevoFestivo].sort());
              setNuevoFestivo("");
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" /> Agregar
          </Button>
          {nuevoFestivo && (
            <p className="text-xs text-muted-foreground">
              Siguiente día hábil:{" "}
              <span className="num">{siguienteHabil(nuevoFestivo, festivos)}</span>
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
